import DotLoader from "@/components/ui/dot-loader";
import { CallHistory } from "@EspaceParkQC/sdk-call-center";
import {
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  ScrollArea,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@EspaceParkQC/sdk-react";
import dayjs from "dayjs";
import { ClockIcon, Minus, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

export default function SidebarHistory(): React.ReactElement {
  const { t } = useTranslation("main");

  return (
    <SidebarGroup>
      <SidebarGroupLabel>{t("sidebar.history")}</SidebarGroupLabel>
      <SidebarGroupContent className="flex flex-col gap-2">
        <SidebarMenu>
          <CallHistory
            render={({ history, hasMore, isLoadingMore, loadMore }) => {
              const hasEntries = Object.entries(history).length > 0;

              if (!hasEntries) {
                return <p className="p-2 text-xs text-muted-foreground">{t("sidebar.no-entries-found")}</p>;
              }

              return (
                <>
                  <ScrollArea>
                    <ul>
                      {Object.entries(history)
                        .reverse()
                        .map(([date, items], index) => (
                          <Collapsible key={date + index} defaultOpen={index === 0} className="group/collapsible">
                            <SidebarMenuItem>
                              <CollapsibleTrigger asChild>
                                <SidebarMenuButton>
                                  <span className="text-xs font-medium">{dayjs(date).format("ddd, LL")}</span>
                                  <Plus className="ml-auto group-data-[state=open]/collapsible:hidden" />
                                  <Minus className="ml-auto group-data-[state=closed]/collapsible:hidden" />
                                </SidebarMenuButton>
                              </CollapsibleTrigger>
                              {items?.calls?.length ? (
                                <CollapsibleContent>
                                  <SidebarMenuSub className="border-none px-0 ml-2 mr-0 py-2 space-y-1">
                                    {items.calls.map((call, callIndex) => {
                                      let computedName = "";
                                      if (call.parkingName) computedName = call.parkingName;
                                      if (call.intercomName) computedName += ` - ${call.intercomName}`;
                                      if (!computedName) computedName = t("sidebar.unknown");
                                      return (
                                        <SidebarMenuSubItem key={call.extension + call.id + callIndex}>
                                          <SidebarMenuSubButton asChild isActive={false} className="block h-auto">
                                            <Link to={`/?hid=${encodeURIComponent(call.id)}`} className="text-xs w-full block space-y-1.5 py-1.5">
                                              <div className="flex items-center gap-1.5">
                                                <ClockIcon className="size-3 text-primary" /> {dayjs(call.date).format("HH:mm:ss")}
                                              </div>
                                              <div className="font-medium">
                                                {t(`sidebar.${call.direction == "inbound" ? "from" : "to"}`)} {computedName}
                                              </div>
                                            </Link>
                                          </SidebarMenuSubButton>
                                        </SidebarMenuSubItem>
                                      );
                                    })}
                                  </SidebarMenuSub>
                                </CollapsibleContent>
                              ) : (
                                <p className="p-2 text-xs text-muted-foreground">{t("sidebar.no-calls-found")}</p>
                              )}
                            </SidebarMenuItem>
                          </Collapsible>
                        ))}
                    </ul>
                  </ScrollArea>
                  {hasMore && (
                    <div className="p-2 border-t">
                      <Button variant="outline" size="sm" className="w-full" onClick={loadMore} disabled={isLoadingMore}>
                        {isLoadingMore ? (
                          <>
                            <DotLoader className="mr-2" />
                            {t("sidebar.loading-more")}
                          </>
                        ) : (
                          t("sidebar.load-more")
                        )}
                      </Button>
                    </div>
                  )}
                </>
              );
            }}
          />
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}
